/**
 * Marketing / ad-attribution query parameters.
 *
 * Single source of truth for the params we treat as campaign attribution.
 * Used to (a) persist signup attribution on the login page and (b) preserve
 * attribution on PostHog `$pageview` URLs.
 *
 * This is an allowlist on purpose: pageview URLs must NOT carry sensitive query
 * params such as OAuth `code`/`state`, `installation_id`, or `token` into
 * PostHog (events + session replay). Only the keys below are ever preserved.
 */
export const TRACKING_PARAM_KEYS = [
  // UTM parameters
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // Google Ads
  'gclid',
  'gclsrc',
  'dclid',
  'wbraid',
  'gbraid',
  // Facebook/Meta
  'fbclid',
  // Microsoft/Bing
  'msclkid',
  // TikTok
  'ttclid',
  // LinkedIn
  'li_fat_id',
  // Twitter/X
  'twclid',
  // General
  'ref',
  'source',
] as const;

/**
 * Given a raw URL query string, return a query string containing ONLY the
 * allowlisted marketing/attribution params, with a leading `?`. Returns an
 * empty string when none are present.
 *
 * Accepts the query string with or without a leading `?`.
 */
export function preserveTrackingParams(search: string): string {
  const input = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const kept = new URLSearchParams();

  for (const key of TRACKING_PARAM_KEYS) {
    const value = input.get(key);
    if (value) {
      kept.set(key, value);
    }
  }

  const result = kept.toString();
  return result ? `?${result}` : '';
}
